import { useMemo } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Area,
  AreaChart,
} from 'recharts';
import {
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  BanknotesIcon,
  ChartPieIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { usePortfolioSummary, usePortfolioHistory } from '../hooks/usePortfolio';
import { useDividendsTotals } from '../hooks/useDividends';
import { useAuth } from '../context/AuthContext';
import { PageLoader } from '../components/common/LoadingSpinner';
import { InoveiTechBanner } from '../components/common/InoveiTechBanner';
import { InfoTooltip } from '../components/common/InfoTooltip';
import {
  hideCurrency,
  hidePercent,
  gainLossClass,
} from '../utils/format';
import { useValuesVisibility } from '../stores/valuesVisibilityStore';
import { ASSET_TYPE_COLORS, ASSET_TYPE_LABELS } from '../types';

export default function DashboardPage() {
  const { user } = useAuth();
  const { isVisible } = useValuesVisibility();
  const { data: summary, isLoading: summaryLoading } = usePortfolioSummary();
  const { data: dividendTotals } = useDividendsTotals();
  const { data: history } = usePortfolioHistory();

  const historyChartData = useMemo(
    () =>
      (history ?? []).map((p) => ({
        month: p.month,
        label: new Date(p.month + '-01').toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }),
        invested: p.invested,
        net: p.net,
      })),
    [history],
  );

  const allocationChartData = useMemo(
    () =>
      Array.isArray(summary?.byType)
        ? summary.byType.map((item) => ({
            name: ASSET_TYPE_LABELS[item.type as keyof typeof ASSET_TYPE_LABELS] || item.type,
            value: item.invested,
            percentage: item.percentage,
            color: ASSET_TYPE_COLORS[item.type as keyof typeof ASSET_TYPE_COLORS] || '#6b7280',
          }))
        : [],
    [summary],
  );

  if (summaryLoading) return <PageLoader />;

  const gainLoss = summary?.gainLoss ?? 0;
  const gainLossPct = summary?.gainLossPercent ?? 0;

  const firstName = user?.name?.split(' ')[0] ?? '';
  const today = new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' });

  return (
    <div className="space-y-6">
      {/* ── InoveiTech banner ── */}
      <InoveiTechBanner firstName={firstName} today={today} />

      {/* Header */}
      <div className="page-header" style={{ marginBottom: 0 }}>
        <p className="page-subtitle">Visão geral do seu portfólio</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="stat-card">
          <span className="stat-label">Total Investido</span>
          <span className="stat-value text-white">
            {hideCurrency(summary?.totalInvested ?? 0, isVisible)}
          </span>
          <span className="text-xs text-white/40">{summary?.assetCount} ativos</span>
        </div>

        <div className="stat-card">
          <span className="stat-label">Valor Atual</span>
          <span className="stat-value text-white">
            {hideCurrency(summary?.currentValue ?? 0, isVisible)}
          </span>
          <span className="text-xs text-white/40">Preço de mercado</span>
        </div>

        <div className="stat-card">
          <span className="stat-label">Lucro / Prejuízo</span>
          <span className={`stat-value ${gainLossClass(gainLoss)}`}>
            {hideCurrency(gainLoss, isVisible)}
          </span>
          <span className={`text-xs ${gainLossClass(gainLossPct)}`}>
            {gainLoss >= 0 ? (
              <ArrowTrendingUpIcon className="inline h-3 w-3 mr-1" />
            ) : (
              <ArrowTrendingDownIcon className="inline h-3 w-3 mr-1" />
            )}
            {hidePercent(gainLossPct, isVisible)}
          </span>
        </div>

        <div className="stat-card">
          <span className="stat-label">Proventos (mês)</span>
          <span className="stat-value text-success-500">
            {hideCurrency(dividendTotals?.monthly ?? 0, isVisible)}
          </span>
          <span className="text-xs text-white/40">
            {hideCurrency(dividendTotals?.yearly ?? 0, isVisible)} no ano
          </span>
        </div>
      </div>

      {/* Evolução Patrimonial */}
      {historyChartData.length > 0 && (
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <ChartBarIcon className="h-5 w-5 text-primary-400" />
            <h2 className="text-sm font-semibold text-white">Evolução Patrimonial</h2>
            <InfoTooltip text="Histórico do total aportado e do valor líquido da sua carteira mês a mês. Permite acompanhar a evolução do patrimônio ao longo do tempo." />
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={historyChartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="gradInvested" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#00d4ff" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#00d4ff" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradNet" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis
                dataKey="label"
                tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) =>
                  v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)
                }
                width={48}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (active && payload?.length) {
                    return (
                      <div className="bg-surface-800 border border-white/10 rounded-lg p-3 text-xs space-y-1">
                        <p className="text-white/60 font-medium">{label}</p>
                        {payload.map((p) => (
                          <p key={p.dataKey} style={{ color: p.color }}>
                            {p.name}: {hideCurrency(Number(p.value), isVisible)}
                          </p>
                        ))}
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Area
                type="monotone"
                dataKey="invested"
                name="Total Aportado"
                stroke="#00d4ff"
                strokeWidth={2}
                fill="url(#gradInvested)"
                dot={false}
              />
              <Area
                type="monotone"
                dataKey="net"
                name="Líquido"
                stroke="#10b981"
                strokeWidth={2}
                fill="url(#gradNet)"
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Allocation Pie Chart */}
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <ChartPieIcon className="h-5 w-5 text-primary-400" />
            <h2 className="text-sm font-semibold text-white">Distribuição por Tipo</h2>
            <InfoTooltip text="Como o seu patrimônio está dividido entre os tipos de ativos: ações, FIIs, criptomoedas, fundos e outros. Ideal para avaliar a diversificação da carteira." />
          </div>

          {allocationChartData.length > 0 ? (
            <div className="flex items-center gap-4">
              <ResponsiveContainer width="50%" height={200}>
                <PieChart>
                  <Pie
                    data={allocationChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {allocationChartData.map((entry, index) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload?.length) {
                        const d = payload[0].payload;
                        return (
                          <div className="bg-surface-800 border border-white/10 rounded-lg p-3 text-xs">
                            <p className="font-medium text-white">{d.name}</p>
                            <p className="text-white/60">{hideCurrency(d.value, isVisible)}</p>
                            <p className="text-primary-400">{d.percentage}%</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>

              <div className="flex-1 space-y-2">
                {allocationChartData.map((item) => (
                  <div key={item.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="text-xs text-white/60">{item.name}</span>
                    </div>
                    <span className="text-xs font-medium text-white">{item.percentage}%</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-48 text-white/30 text-sm">
              Nenhum ativo cadastrado
            </div>
          )}
        </div>

        {/* Top Performers */}
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <ArrowTrendingUpIcon className="h-5 w-5 text-success-500" />
            <h2 className="text-sm font-semibold text-white">Melhores Performances</h2>
            <InfoTooltip text="Os 5 ativos com maior valorização percentual em relação ao seu preço médio de compra. Indica quais investimentos estão gerando mais retorno." />
          </div>

          <div className="space-y-3">
            {summary?.topPerformers?.slice(0, 5).map((item) => (
              <div key={item.code} className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-white font-mono">{item.code}</span>
                  <span className="ml-2 text-xs text-white/40">{item.name}</span>
                </div>
                <div className="text-right">
                  <p className={`text-sm font-medium ${gainLossClass(item.gainLossPercent)}`}>
                    {hidePercent(item.gainLossPercent, isVisible)}
                  </p>
                  <p className={`text-xs ${gainLossClass(item.gainLoss)}`}>
                    {hideCurrency(item.gainLoss, isVisible)}
                  </p>
                </div>
              </div>
            ))}

            {(!summary?.topPerformers || summary.topPerformers.length === 0) && (
              <p className="text-white/30 text-sm text-center py-8">
                Sem dados de performance
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Dividends */}
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <BanknotesIcon className="h-5 w-5 text-success-500" />
            <h2 className="text-sm font-semibold text-white">Proventos Recentes</h2>
            <InfoTooltip text="Últimos dividendos, JCP, rendimentos e amortizações recebidos na sua carteira. Reflete a renda passiva gerada pelos seus investimentos." position="bottom" />
          </div>

          <div className="space-y-3">
            {summary?.recentDividends?.slice(0, 5).map((div) => (
              <div key={div.id} className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-white font-mono">
                    {div.assets?.code}
                  </span>
                  <span className="ml-2 text-xs text-white/40">{div.type}</span>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-success-500">
                    {hideCurrency(div.totalValue, isVisible)}
                  </p>
                  <p className="text-xs text-white/40">{div.paymentDate}</p>
                </div>
              </div>
            ))}

            {(!summary?.recentDividends || summary.recentDividends.length === 0) && (
              <p className="text-white/30 text-sm text-center py-8">
                Nenhum provento recente
              </p>
            )}
          </div>
        </div>

        {/* Worst Performers */}
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <ArrowTrendingDownIcon className="h-5 w-5 text-danger-500" />
            <h2 className="text-sm font-semibold text-white">Piores Performances</h2>
            <InfoTooltip text="Os 5 ativos com maior desvalorização percentual em relação ao preço médio de compra. Indica quais investimentos estão gerando menos retorno." position="bottom" />
          </div>

          <div className="space-y-3">
            {summary?.worstPerformers?.slice(0, 5).map((item) => (
              <div key={item.code} className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-white font-mono">{item.code}</span>
                  <span className="ml-2 text-xs text-white/40">{item.name}</span>
                </div>
                <div className="text-right">
                  <p className={`text-sm font-medium ${gainLossClass(item.gainLossPercent)}`}>
                    {hidePercent(item.gainLossPercent, isVisible)}
                  </p>
                  <p className={`text-xs ${gainLossClass(item.gainLoss)}`}>
                    {hideCurrency(item.gainLoss, isVisible)}
                  </p>
                </div>
              </div>
            ))}

            {(!summary?.worstPerformers || summary.worstPerformers.length === 0) && (
              <p className="text-white/30 text-sm text-center py-8">
                Sem dados de performance
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
